import { logger } from './logger';
import { createLogContext } from './loggerUtils';
import { storage } from './storage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { RatingPromptState } from '../types/storage';
import { getPlatform } from '../infrastructure/platform';

/**
 * "Rate us on the Chrome Web Store" prompt logic.
 *
 * Chrome has no in-extension review API (unlike iOS/Android), so the mechanism
 * is: count prompt uses, and once the user has gotten real value out of the
 * extension, show a small dismissible card that deep-links to the store
 * listing's review page. All state lives under STORAGE_KEYS.RATING_PROMPT_STATE.
 */

/** Prompt uses before the rating card first becomes eligible to show */
export const RATING_PROMPT_MIN_USES = 10;
/** Further uses before re-asking after "Maybe later" */
export const RATING_PROMPT_LATER_REARM_USES = 25;

export const SUPPORT_EMAIL = 'support@TopPromptLibrary.com';

const MODULE = 'ratingPrompt';

const DEFAULT_STATE: RatingPromptState = {
  usageCount: 0,
  status: 'pending',
  remindAtCount: RATING_PROMPT_MIN_USES,
};

// The review deep link only exists on the Chrome Web Store; the Firefox build
// would need an AMO listing URL (none is published yet), so the prompt is
// Chrome-only for now (the capability is computed by the platform adapter).
function isChromeExtensionContext(): boolean {
  return getPlatform().capabilities.storeReview;
}

/**
 * The extension's Chrome Web Store review page. Hardcoded rather than built
 * from chrome.runtime.id: an unpacked/dev build gets a locally generated id
 * that doesn't match the published listing, so a runtime-derived URL 404s
 * anywhere except the store-installed build.
 */
const WEB_STORE_REVIEW_URL =
  'https://chromewebstore.google.com/detail/top-prompt-library/aonajceephccckeijagkklpaeidkmmol/reviews';

export function getReviewUrl(): string {
  return WEB_STORE_REVIEW_URL;
}

async function readState(): Promise<RatingPromptState> {
  const stored = await storage.get(STORAGE_KEYS.RATING_PROMPT_STATE);
  return stored ? { ...DEFAULT_STATE, ...stored } : { ...DEFAULT_STATE };
}

async function writeState(state: RatingPromptState): Promise<void> {
  await storage.set(STORAGE_KEYS.RATING_PROMPT_STATE, state);
}

/**
 * Record one prompt use (copy/insert). Returns true when the rating card
 * should be shown now. Never throws — rating bookkeeping must not break the
 * copy path.
 */
export async function recordPromptUsage(): Promise<boolean> {
  if (!isChromeExtensionContext()) return false;
  try {
    const state = await readState();
    if (state.status === 'rated' || state.status === 'declined') {
      return false;
    }
    const next = { ...state, usageCount: state.usageCount + 1 };
    await writeState(next);
    return next.usageCount >= next.remindAtCount;
  } catch (error) {
    logger.error(
      'Failed to record prompt usage for rating prompt',
      error instanceof Error ? error : new Error('Unknown error'),
      createLogContext('recordPromptUsage', { module: MODULE })
    );
    return false;
  }
}

/** "Maybe later" — re-arm after RATING_PROMPT_LATER_REARM_USES further uses. */
export async function deferRatingPrompt(): Promise<void> {
  try {
    const state = await readState();
    await writeState({
      ...state,
      status: 'later',
      remindAtCount: state.usageCount + RATING_PROMPT_LATER_REARM_USES,
    });
  } catch (error) {
    logger.error(
      'Failed to defer rating prompt',
      error instanceof Error ? error : new Error('Unknown error'),
      createLogContext('deferRatingPrompt', { module: MODULE })
    );
  }
}

/** "No thanks" / routed to feedback — never ask again. */
export async function declineRatingPrompt(): Promise<void> {
  try {
    const state = await readState();
    await writeState({ ...state, status: 'declined' });
  } catch (error) {
    logger.error(
      'Failed to decline rating prompt',
      error instanceof Error ? error : new Error('Unknown error'),
      createLogContext('declineRatingPrompt', { module: MODULE })
    );
  }
}

/**
 * Open the Web Store review page and mark the user as rated. State is
 * persisted BEFORE the tab opens: opening a tab closes the popup document,
 * which would otherwise lose the write and re-prompt a user who already rated.
 */
export async function openReviewPage(): Promise<void> {
  try {
    const state = await readState();
    await writeState({ ...state, status: 'rated' });
    await getPlatform().actions.openExternalUrl(getReviewUrl());
  } catch (error) {
    logger.error(
      'Failed to open review page',
      error instanceof Error ? error : new Error('Unknown error'),
      createLogContext('openReviewPage', { module: MODULE })
    );
  }
}
